import { config } from 'config';
import { createApolloClient } from 'helpers/apolloClient';
import type { ErrorReportInput } from 'helpers/formatErrorReport';
import { useAuth0 } from '@auth0/auth0-react';
import { useGlobalError } from 'hooks/useGlobalError';
import { useCallback, useMemo, useRef } from 'react';

export const useApollo = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [, setGlobalError] = useGlobalError();

  // Keep a ref so the Apollo Client isn't rebuilt when the setter identity changes.
  const setGlobalErrorRef = useRef(setGlobalError);
  setGlobalErrorRef.current = setGlobalError;

  const loginWithRedirectRef = useRef(loginWithRedirect);
  loginWithRedirectRef.current = loginWithRedirect;

  const fetchToken = useCallback(
    async (forceRefresh = false) => {
      try {
        return await getAccessTokenSilently(forceRefresh ? { cacheMode: 'off' } : undefined);
      } catch (error) {
        console.error('Error getting access token:', error);
        return null;
      }
    },
    [getAccessTokenSilently],
  );

  const onGlobalError = useCallback((message: string, diagnostics?: ErrorReportInput) => {
    setGlobalErrorRef.current({ messages: message, diagnostics });
  }, []);

  const onAuthRequired = useCallback(() => {
    loginWithRedirectRef.current();
  }, []);

  const uri = `${config.apiUrl}/graphql`;
  return useMemo(
    () => createApolloClient(uri, fetchToken, onGlobalError, onAuthRequired),
    [fetchToken, onGlobalError, onAuthRequired, uri],
  );
};
